/**
 * AnalyticsService — wraps Firebase Analytics + Crashlytics.
 *
 * On native platforms (iOS / Android):
 *   - logEvent / setUserId delegate to @capacitor-firebase/analytics (FirebaseAnalytics.logEvent).
 *   - recordError delegates to @capacitor-firebase/crashlytics (FirebaseCrashlytics.recordException).
 *   Firebase is configured entirely via native config files (no JS SDK key needed):
 *     - Android: google-services.json  (place in android/app/)
 *     - iOS:     GoogleService-Info.plist  (place in ios/App/App/)
 *
 * On web (browser):
 *   - If VITE_FIREBASE_PROJECT_ID + VITE_FIREBASE_MEASUREMENT_ID are set, the Firebase
 *     JS SDK (firebase/analytics) is used via getFirebaseAnalytics() from firebase.ts.
 *   - Otherwise falls back to console.log — no external SDK is loaded.
 *
 * NOTE: initFirebase() must be called before AnalyticsService.init() for web SDK to work.
 */

import { Capacitor } from '@capacitor/core'
import { logEvent as fbLogEvent } from 'firebase/analytics'
import { getFirebaseAnalytics } from '../config/firebase.js'

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>

export class AnalyticsService {
  private static instance: AnalyticsService | null = null
  private enabled: boolean = false
  private isNative: boolean = false

  private constructor() {}

  static getInstance(): AnalyticsService {
    if (AnalyticsService.instance === null) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  /**
   * Initialise the analytics service.
   * Pass `enabled=true` to activate Firebase on native, or console-logging on web.
   * Pass `enabled=false` to silence all tracking (e.g. for users who opt out).
   */
  init(enabled: boolean): void {
    this.enabled = enabled
    this.isNative = Capacitor.isNativePlatform()

    if (!this.enabled) return

    if (this.isNative) {
      // Firebase Analytics is initialised automatically by the native plugin
      // once google-services.json / GoogleService-Info.plist are in place.
      // We explicitly enable collection here to be consistent regardless of
      // any previous setEnabled(false) calls across app sessions.
      import('@capacitor-firebase/analytics')
        .then(({ FirebaseAnalytics }) => FirebaseAnalytics.setEnabled({ enabled: true }))
        .catch((err) => console.error('[Analytics] FirebaseAnalytics.setEnabled failed:', err))
      console.log('[Analytics] Firebase Analytics initialised (native)')
    } else {
      console.log('[Analytics] Initialized — console-only tracking (web)')
    }
  }

  /**
   * Log a named analytics event with optional parameters.
   * On native: delegates to @capacitor-firebase/analytics FirebaseAnalytics.logEvent().
   * On web with Firebase configured: delegates to firebase/analytics logEvent().
   * On web without Firebase: logs to console.
   */
  track(name: string, params?: AnalyticsParams): void {
    if (!this.enabled) return

    if (this.isNative) {
      // Convert params to the string-only format expected by the Capacitor Firebase plugin.
      const firebaseParams: Record<string, string> = {}
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value != null) {
            firebaseParams[key] = String(value)
          }
        }
      }
      import('@capacitor-firebase/analytics')
        .then(({ FirebaseAnalytics }) =>
          FirebaseAnalytics.logEvent({ name, params: firebaseParams }),
        )
        .catch((err) => console.error(`[Analytics] logEvent("${name}") failed:`, err))
    } else {
      // Web platform: use Firebase JS SDK if initialised, otherwise console fallback.
      const webAnalytics = getFirebaseAnalytics()
      if (webAnalytics !== null) {
        try {
          fbLogEvent(webAnalytics, name, params)
        } catch {
          // Firebase not configured — console fallback
          console.log('[Analytics]', name, params ?? {})
        }
      } else {
        console.log('[Analytics]', name, params ?? {})
      }
    }
  }

  /**
   * Associate subsequent events with a user ID.
   * On native: delegates to FirebaseAnalytics.setUserId().
   * On web: no-op (avoid logging PII to console).
   */
  setUserId(id: string): void {
    if (!this.enabled) return

    if (this.isNative) {
      import('@capacitor-firebase/analytics')
        .then(({ FirebaseAnalytics }) => FirebaseAnalytics.setUserId({ userId: id }))
        .catch((err) => console.error('[Analytics] setUserId failed:', err))
    }
    // Intentionally silent on web — do not log user IDs to the browser console.
  }

  /**
   * Record a non-fatal error for Crashlytics.
   * On native: delegates to FirebaseCrashlytics.recordException().
   * On web: logs to console.error.
   *
   * @param error   The error object or message to record.
   * @param context Optional label prepended to the message (e.g. 'MatchEngine').
   */
  async recordError(error: Error, context?: string): Promise<void> {
    try {
      const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics')
      await FirebaseCrashlytics.recordException({
        message: context ? `[${context}] ${error.message}` : error.message,
      })
    } catch {
      console.error('[Analytics] recordError:', context, error)
    }
  }

  // ---------------------------------------------------------------------------
  // Convenience event methods — keep names in sync with the analytics plan.
  // ---------------------------------------------------------------------------

  trackMatchStart(mode: string, mapId: string): void {
    this.track('match_start', { mode, map_id: mapId })
  }

  trackMatchEnd(outcome: 'win' | 'lose' | 'draw', durationMs: number): void {
    this.track('match_end', { outcome, duration_ms: durationMs })
  }

  trackPackOpened(packType: string): void {
    this.track('pack_opened', { pack_type: packType })
  }

  trackShopOpened(tab: string): void {
    this.track('shop_opened', { tab })
  }

  // ---------------------------------------------------------------------------
  // Typed event methods (Phase 4 analytics plan)
  // ---------------------------------------------------------------------------

  logMatchStart(params: {
    mode: string
    mapId: string
    deckId: string | null
    gameMode: 'bot' | 'pvp' | 'campaign'
  }): void {
    this.track('match_start', {
      mode: params.mode,
      map_id: params.mapId,
      deck_id: params.deckId ?? '',
      game_mode: params.gameMode,
    })
  }

  logMatchEnd(params: {
    outcome: 'win' | 'lose' | 'draw'
    durationSecs: number
    mode: string
    goldEarned: number
    towersBuilt: number
    unitsSent: number
  }): void {
    this.track('match_end', {
      outcome: params.outcome,
      duration_secs: params.durationSecs,
      mode: params.mode,
      gold_earned: params.goldEarned,
      towers_built: params.towersBuilt,
      units_sent: params.unitsSent,
    })
  }

  logTowerAction(params: {
    action: 'place' | 'upgrade' | 'demolish'
    towerId: string
    element: string
    level?: number
  }): void {
    this.track('tower_action', {
      action: params.action,
      tower_id: params.towerId,
      element: params.element,
      level: params.level ?? 1,
    })
  }

  logUnitSent(params: { unitId: string; element: string }): void {
    this.track('unit_sent', {
      unit_id: params.unitId,
      element: params.element,
    })
  }

  logPackOpened(params: { packType: string; cardCount: number; rarities: string[] }): void {
    this.track('pack_opened', {
      pack_type: params.packType,
      card_count: params.cardCount,
      rarities: params.rarities.join(','),
    })
  }

  logScreenView(screenName: string): void {
    this.track('screen_view', { firebase_screen: screenName })
  }

  logLevelUp(params: { newLevel: number; totalXp: number }): void {
    this.track('level_up', {
      new_level: params.newLevel,
      total_xp: params.totalXp,
    })
  }

  logQuestCompleted(params: { questId: string; questType: string }): void {
    this.track('quest_completed', {
      quest_id: params.questId,
      quest_type: params.questType,
    })
  }

  logShopOpen(tab: string): void {
    this.track('shop_open', { tab })
  }

  logAuthEvent(params: { event: 'register' | 'login' | 'logout'; method?: string }): void {
    this.track('auth_event', {
      event: params.event,
      method: params.method ?? '',
    })
  }
}

export default AnalyticsService
